import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const StoichiometryCalculator = () => {
  const [stoichiometryData, setStoichiometryData] = useState({
    reactantMass: '',
    reactantMolarMass: '',
    productCoefficient: '',
    reactantCoefficient: '',
    productMolarMass: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setStoichiometryData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateStoichiometry = () => {
    const { reactantMass, reactantMolarMass, productCoefficient, reactantCoefficient, productMolarMass } = stoichiometryData;
    
    const mass = parseFloat(reactantMass) || 0;
    const molarMassReactant = parseFloat(reactantMolarMass) || 0;
    const coefficientProduct = parseFloat(productCoefficient) || 0;
    const coefficientReactant = parseFloat(reactantCoefficient) || 0;
    const molarMassProduct = parseFloat(productMolarMass) || 0;

    if (mass === 0 || isNaN(mass)) {
      setError('Please enter a valid mass of reactant');
      return;
    }

    if (molarMassReactant === 0 || isNaN(molarMassReactant)) {
      setError('Please enter a valid molar mass of reactant');
      return;
    }

    if (coefficientProduct === 0 || isNaN(coefficientProduct)) {
      setError('Please enter a valid coefficient of product');
      return;
    }

    if (coefficientReactant === 0 || isNaN(coefficientReactant)) {
      setError('Please enter a valid coefficient of reactant');
      return;
    }

    if (molarMassProduct === 0 || isNaN(molarMassProduct)) {
      setError('Please enter a valid molar mass of product');
      return;
    }

    // Calculate moles of reactant
    const molesReactant = mass / molarMassReactant;

    // Calculate moles of product using stoichiometry
    const molesProduct = (molesReactant * coefficientProduct) / coefficientReactant;

    // Calculate mass of product
    const massProduct = molesProduct * molarMassProduct;

    const calculationSteps = [
      'Step 1: Prepare the values',
      `Mass of reactant: ${mass} g`,
      `Molar mass of reactant: ${molarMassReactant} g/mol`,
      `Coefficient of product: ${coefficientProduct}`,
      `Coefficient of reactant: ${coefficientReactant}`,
      `Molar mass of product: ${molarMassProduct} g/mol`,
      '\nStep 2: Calculate moles of reactant',
      `Moles of reactant = Mass of reactant ÷ Molar mass of reactant`,
      `Moles of reactant = ${mass} ÷ ${molarMassReactant} = ${molesReactant.toFixed(3)} mol`,
      '\nStep 3: Calculate moles of product using stoichiometry',
      `Moles of product = (Moles of reactant × Coefficient of product) ÷ Coefficient of reactant`,
      `Moles of product = (${molesReactant.toFixed(3)} × ${coefficientProduct}) ÷ ${coefficientReactant} = ${molesProduct.toFixed(3)} mol`,
      '\nStep 4: Calculate mass of product',
      `Mass of product = Moles of product × Molar mass of product`,
      `Mass of product = ${molesProduct.toFixed(3)} × ${molarMassProduct} = ${massProduct.toFixed(3)} g`
    ];

    const newResults = {
      molesReactant: molesReactant.toFixed(3),
      molesProduct: molesProduct.toFixed(3),
      massProduct: massProduct.toFixed(3),
      calculationSteps
    };

    setResults(newResults);

    // Update chart data
    setChartData({
      bar: {
        labels: ['Moles of Reactant', 'Moles of Product', 'Mass of Product'],
        datasets: [{
          label: 'Values',
          data: [molesReactant, molesProduct, massProduct],
          backgroundColor: [
            'rgba(75, 192, 192, 0.5)',
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 99, 132, 0.5)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)'
          ],
          borderWidth: 1
        }]
      }
    });
  };

  const exampleData = {
    reactantMass: 10,
    reactantMolarMass: 50,
    productCoefficient: 2,
    reactantCoefficient: 1,
    productMolarMass: 100,
    molesReactant: 0.2,
    molesProduct: 0.4,
    massProduct: 40
  };

  return (
    <>
     <Helmet>
      {/* Basic SEO Meta Tags */}
      <title>Stoichiometry Calculator - Reaction Analysis</title>
      <meta name="description" content="Calculate stoichiometry using reactant mass, molar masses, and coefficients. Understand reactions and stoichiometry concepts with our interactive stoichiometry calculator." />
      <meta name="keywords" content="stoichiometry calculator, reaction analysis, chemistry calculator, stoichiometry, chemical reactions, laboratory work" />
      <meta name="author" content="Your Company Name" />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Open Graph Meta Tags for Facebook and LinkedIn */}
      <meta property="og:title" content="Stoichiometry Calculator - Reaction Analysis" />
      <meta property="og:description" content="Calculate stoichiometry using reactant mass, molar masses, and coefficients. Understand reactions and stoichiometry concepts with our interactive stoichiometry calculator." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://yourdomain.com/stoichiometry-calculator" />
      <meta property="og:image" content="https://yourdomain.com/stoichiometry-calculator-preview.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:site_name" content="Chemistry Calculators" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@your_twitter_handle" />
      <meta name="twitter:creator" content="@your_twitter_handle" />
      <meta name="twitter:title" content="Stoichiometry Calculator - Reaction Analysis" />
      <meta name="twitter:description" content="Calculate stoichiometry using reactant mass, molar masses, and coefficients. Understand reactions and stoichiometry concepts with our interactive stoichiometry calculator." />
      <meta name="twitter:image" content="https://yourdomain.com/stoichiometry-calculator-preview.jpg" />
      <meta name="twitter:image:alt" content="Stoichiometry Calculator Preview" />
      
      {/* Pinterest Meta Tags */}
      <meta name="pinterest" content="nopin" />
      <meta name="pinterest-rich-pin" content="true" />
      
      {/* Schema.org Markup for Google+ */}
      <meta itemprop="name" content="Stoichiometry Calculator - Reaction Analysis" />
      <meta itemprop="description" content="Calculate stoichiometry using reactant mass, molar masses, and coefficients. Understand reactions and stoichiometry concepts with our interactive stoichiometry calculator." />
      <meta itemprop="image" content="https://yourdomain.com/stoichiometry-calculator-preview.jpg" />
      
      {/* Additional Meta Tags for Better Sharing */}
      <meta name="theme-color" content="#ffffff" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Stoichiometry Calculator" />
      
      
    </Helmet>

    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">⚗️</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Stoichiometry Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Reaction Analysis</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          Calculate the stoichiometry of a reaction using the mass of reactant, molar masses, and coefficients.
          This calculator helps you understand reaction stoichiometry and chemical concepts.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="reactantMass">
            Mass of Reactant (g):
          </label>
          <input
            type="number"
            id="reactantMass"
            name="reactantMass"
            value={stoichiometryData.reactantMass}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter mass of reactant in grams"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="reactantMolarMass">
            Molar Mass of Reactant (g/mol):
          </label>
          <input
            type="number"
            id="reactantMolarMass"
            name="reactantMolarMass"
            value={stoichiometryData.reactantMolarMass}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter molar mass of reactant in g/mol"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="productCoefficient">
            Coefficient of Product:
          </label>
          <input
            type="number"
            id="productCoefficient"
            name="productCoefficient"
            value={stoichiometryData.productCoefficient}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter coefficient of product"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="reactantCoefficient">
            Coefficient of Reactant:
          </label>
          <input
            type="number"
            id="reactantCoefficient"
            name="reactantCoefficient"
            value={stoichiometryData.reactantCoefficient}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter coefficient of reactant"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="productMolarMass">
            Molar Mass of Product (g/mol):
          </label>
          <input
            type="number"
            id="productMolarMass"
            name="productMolarMass"
            value={stoichiometryData.productMolarMass}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter molar mass of product in g/mol"
          />
        </div>

        <button
          onClick={calculateStoichiometry}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Stoichiometry
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="grid grid-cols-1 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Reaction Properties</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>Moles of Reactant (mol)</span>
                    <span className="font-medium">{results.molesReactant}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Moles of Product (mol)</span>
                    <span className="font-medium">{results.molesProduct}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Mass of Product (g)</span>
                    <span className="font-medium">{results.massProduct}</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-12 mt-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Bar Chart</h4>
                <div className="h-96">
                  <Bar
                    data={chartData.bar}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Value'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Reaction Property'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Reaction Properties'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
              <div className="space-y-4">
                {results.calculationSteps.map((step, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Calculation <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Here's an example calculation using a hypothetical reaction:
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Sample Data:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>Mass of reactant: {exampleData.reactantMass} g</li>
              <li>Molar mass of reactant: {exampleData.reactantMolarMass} g/mol</li>
              <li>Coefficient of product: {exampleData.productCoefficient}</li>
              <li>Coefficient of reactant: {exampleData.reactantCoefficient}</li>
              <li>Molar mass of product: {exampleData.productMolarMass} g/mol</li>
            </ul>
            
            <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
            <ol className="list-decimal pl-8 space-y-4">
              <li>
                <p>Calculate moles of reactant:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Moles = Mass ÷ Molar mass</li>
                  <li>Moles = {exampleData.reactantMass}
                  ÷ {exampleData.reactantMolarMass} = {exampleData.molesReactant} mol</li>
                </ul>
              </li>
              <li>
                <p>Calculate moles of product:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Moles of product = (Moles of reactant × Coefficient of product) ÷ Coefficient of reactant</li>
                  <li>Moles of product = ({exampleData.molesReactant} × {exampleData.productCoefficient}) ÷ {exampleData.reactantCoefficient} = {exampleData.molesProduct} mol</li>
                </ul>
              </li>
              <li>
                <p>Calculate mass of product:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Mass of product = Moles of product × Molar mass of product</li>
                  <li>Mass of product = {exampleData.molesProduct} × {exampleData.productMolarMass} = {exampleData.massProduct} g</li>
                </ul>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Stoichiometry Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Stoichiometry Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Gather Reaction Data</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Measure the mass of reactant accurately</li>
                <li>Find the molar mass of the reactant</li>
                <li>Determine the coefficients of reactant and product from the balanced equation</li>
                <li>Find the molar mass of the product</li>
                <li>Ensure all measurements and values are precise</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Reaction Information</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Input the mass of reactant in grams</li>
                <li>Enter the molar mass of the reactant in g/mol</li>
                <li>Specify the coefficients of reactant and product</li>
                <li>Enter the molar mass of the product in g/mol</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Review the calculated moles of reactant and product</li>
                <li>Check the mass of the product</li>
                <li>Use the bar chart for visual analysis</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-6">
            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What is stoichiometry?</h3>
              <p className="text-gray-600">
                Stoichiometry is the area of chemistry that involves calculating the quantities of reactants and products in chemical reactions. 
                It is based on the balanced chemical equation for the reaction.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Why is stoichiometry important in chemistry?</h3>
              <p className="text-gray-600">
                Stoichiometry is crucial in chemistry because it:
                <ul className="list-disc pl-6 mt-2">
                  <li>Helps in determining the correct proportions of reactants and products</li>
                  <li>Ensures efficient use of reactants</li>
                  <li>Is essential for scaling up reactions for industrial purposes</li>
                  <li>Helps in understanding the yield of reactions</li>
                </ul>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">How do I balance a chemical equation?</h3>
              <p className="text-gray-600">
                Balancing a chemical equation involves ensuring that the number of atoms of each element is the same on both sides of the equation.
                Steps to balance an equation:
                <ol className="list-decimal pl-6 mt-2">
                  <li>Write the unbalanced equation</li>
                  <li>Count the number of atoms of each element on both sides</li>
                  <li>Add coefficients to balance the atoms (starting with the most complex molecule)</li>
                  <li>Ensure all coefficients are in the lowest possible ratio</li>
                  <li>Check the balance by recounting the atoms</li>
                </ol>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What if I don't know the molar mass of my compound?</h3>
              <p className="text-gray-600">
                You can find the molar mass of a compound by:
                <ol className="list-decimal pl-6 mt-2">
                  <li>Adding up the atomic masses of all atoms in the molecule</li>
                  <li>Looking it up in a chemistry reference book</li>
                  <li>Using online chemical databases</li>
                </ol>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Why are my results different from expected?</h3>
              <p className="text-gray-600">
                If your results seem unexpected, check:
                <ul className="list-disc pl-6 mt-2">
                  <li>If you've entered the correct molar masses and coefficients</li>
                  <li>If your mass measurement is accurate</li>
                  <li>If all values are in the correct units</li>
                </ul>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Can I use this calculator for limiting reactant problems?</h3>
              <p className="text-gray-600">
                Yes, this calculator can be used for limiting reactant problems by:
                <ol className="list-decimal pl-6 mt-2">
                  <li>Calculating the moles of each reactant</li>
                  <li>Determining which reactant is limiting by comparing the mole ratios</li>
                  <li>Using the limiting reactant to calculate the maximum product formed</li>
                </ol>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Is stoichiometry affected by temperature?</h3>
              <p className="text-gray-600">
                Stoichiometry itself is not affected by temperature as it deals with the ratios of reactants and products. However, reaction rates and equilibrium positions can be temperature-dependent, which may impact the practical yield of the reaction.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-semibold mb-2">Visual Analysis</h3>
              <p className="text-gray-600">Interactive bar charts for easy visualization of reaction properties</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-lg">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-lg font-semibold mb-2">Precise Calculations</h3>
              <p className="text-gray-600">Accurate stoichiometry calculations with step-by-step breakdown</p>
            </div>
            <div className="text-center p-6 bg-yellow-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Unit Conversion</h3>
              <p className="text-gray-600">Automatic handling of molar masses and coefficients</p>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-lg">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="text-lg font-semibold mb-2">Educational Value</h3>
              <p className="text-gray-600">Detailed explanations of chemistry concepts</p>
            </div>
            <div className="text-center p-6 bg-pink-50 rounded-lg">
              <div className="text-4xl mb-4">⚙️</div>
              <h3 className="text-lg font-semibold mb-2">User-Friendly</h3>
              <p className="text-gray-600">Intuitive interface with clear input fields</p>
            </div>
            <div className="text-center p-6 bg-orange-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Real-time Updates</h3>
              <p className="text-gray-600">Instant calculation results as you type</p>
            </div>
          </div>

          <div className="mt-12">
            <h3 className="text-xl font-semibold mb-4">Key Benefits</h3>
            <ul className="list-disc pl-8 space-y-2">
              <li>Save time with automatic calculations</li>
              <li>Reduce calculation errors with built-in validation</li>
              <li>Learn chemistry concepts through practical examples</li>
              <li>Accessible from any device</li>
              <li>Free to use for educational and professional purposes</li>
              <li>Perfect for students, teachers, and professionals</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Stoichiometry <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Stoichiometry is a fundamental concept in chemistry that helps in:
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Chemical Reactions:</span> Determining the correct proportions of reactants and products
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Solution Preparation:</span> Making accurate solutions and mixtures
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Laboratory Work:</span> Ensuring consistent reaction yields
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Industrial Processes:</span> Controlling manufacturing processes and optimizing yields
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: Stoichiometry calculations are essential for understanding the quantitative aspects of chemical reactions and ensuring efficient use of reactants.
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default StoichiometryCalculator;